import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Search, RefreshCw, Award } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { ReportHeader } from '../../components/ReportHeader';
import { ExportButton } from '../../components/ExportButton';
import { CollapsibleSection } from '../../components/CollapsibleSection';

const API = process.env.REACT_APP_BACKEND_URL;

const EMPTY_FILTERS = {
  from_date: '', to_date: '',
  target_audience: '', ai_course: '',
  region: '', language: '', solution_area: '',
  partner_name: '', search: '',
};

function Pagination({ page, totalPages, total, pageSize, onPage }) {
  if (totalPages <= 1) return null;
  const pages = [];
  const start = Math.max(1, page - 2);
  const end = Math.min(totalPages, page + 2);
  for (let i = start; i <= end; i++) pages.push(i);

  return (
    <div className="pagination" data-testid="pagination">
      <span className="pagination-info">
        {((page - 1) * pageSize + 1).toLocaleString()}–{Math.min(page * pageSize, total).toLocaleString()} of {total.toLocaleString()} records
      </span>
      <button className="page-btn" disabled={page <= 1} onClick={() => onPage(1)} data-testid="first-page-btn">«</button>
      <button className="page-btn" disabled={page <= 1} onClick={() => onPage(page - 1)} data-testid="prev-page-btn">‹</button>
      {pages.map(p => (
        <button key={p} className={`page-btn${p === page ? ' active' : ''}`} onClick={() => onPage(p)} data-testid={`page-btn-${p}`}>{p}</button>
      ))}
      <button className="page-btn" disabled={page >= totalPages} onClick={() => onPage(page + 1)} data-testid="next-page-btn">›</button>
      <button className="page-btn" disabled={page >= totalPages} onClick={() => onPage(totalPages)} data-testid="last-page-btn">»</button>
    </div>
  );
}

export default function CompletionReport() {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [applied, setApplied] = useState({ from_date: '', to_date: '' });
  const [stats, setStats] = useState(null);
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [filterOptions, setFilterOptions] = useState({});
  const [piiAllowed, setPiiAllowed] = useState(false); // fail-closed default

  // Load filter options + pii_allowed once
  useEffect(() => {
    axios.get(`${API}/api/reports/filters`, { withCredentials: true })
      .then(r => setFilterOptions(r.data))
      .catch(console.error);
    axios.get(`${API}/api/auth/me`, { withCredentials: true })
      .then(r => setPiiAllowed(r.data?.pii_allowed === true))
      .catch(() => setPiiAllowed(false));
  }, []);

  const buildParams = useCallback((f, p = 1) => {
    const q = { page: p, page_size: 50 };
    if (f.from_date) q.from_date = f.from_date;
    if (f.to_date) q.to_date = f.to_date;
    if (f.target_audience) q.target_audience = f.target_audience;
    if (f.ai_course) q.ai_course = f.ai_course;
    if (f.region) q.region = f.region;
    if (f.language) q.language = f.language;
    if (f.solution_area) q.solution_area = f.solution_area;
    if (f.partner_name) q.partner_name = f.partner_name;
    if (f.search) q.search = f.search;
    return q;
  }, []);

  const fetchData = useCallback(async (f, p = 1) => {
    setLoading(true);
    const params = buildParams(f, p);
    try {
      const [statsRes, listRes] = await Promise.all([
        axios.get(`${API}/api/reports/completion/stats`, { params, withCredentials: true }),
        axios.get(`${API}/api/reports/completion/list`, { params, withCredentials: true }),
      ]);
      setStats(statsRes.data);
      setData(listRes.data.data);
      setTotal(listRes.data.total);
      setTotalPages(listRes.data.total_pages);
      setPage(p);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => {
    fetchData(applied, 1);
  }, []); // eslint-disable-line

  const handlePeriodChange = useCallback(({ startDate, endDate }) => {
    const f = { ...EMPTY_FILTERS, from_date: startDate || '', to_date: endDate || '' };
    setFilters(f);
    setApplied(f);
    fetchData(f, 1);
  }, []); // eslint-disable-line

  const handleApply = () => {
    setApplied(filters);
    fetchData(filters, 1);
  };

  const handleReset = () => {
    setFilters(EMPTY_FILTERS);
    setApplied(EMPTY_FILTERS);
    fetchData(EMPTY_FILTERS, 1);
  };

  const setF = (key, val) => setFilters(f => ({ ...f, [key]: val }));

  const formatDate = d => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

  const trend = stats?.monthly_trend || [];

  return (
    <div data-testid="completion-report-page">
      <ReportHeader
        reportTitle="Insights Cube"
        primaryTable="completions"
        onChange={handlePeriodChange}
      />
      <div className="page-header">
        <h1>Insights Cube</h1>
        <p>Course completions across all programs and sources</p>
      </div>

      {/* KPI Stats — always visible */}
      <div className="stats-grid">
        <div className="stat-card" data-testid="stat-total-completions">
          <div className="stat-label">Total Completions</div>
          <div className="stat-value blue">{(stats?.total_completions || 0).toLocaleString()}</div>
        </div>
        <div className="stat-card" data-testid="stat-ai-completions">
          <div className="stat-label">AI Completions</div>
          <div className="stat-value green">{(stats?.ai_completions || 0).toLocaleString()}</div>
        </div>
        <div className="stat-card" data-testid="stat-unique-learners">
          <div className="stat-label">Unique Learners</div>
          <div className="stat-value">{(stats?.unique_learners || 0).toLocaleString()}</div>
        </div>
        <div className="stat-card" data-testid="stat-unique-courses">
          <div className="stat-label">Unique Courses</div>
          <div className="stat-value purple">{(stats?.unique_courses || 0).toLocaleString()}</div>
        </div>
        <div className="stat-card" data-testid="stat-badge-completions" style={{ borderTop: '3px solid #E8A840' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
            <Award size={15} color="#E8A840" />
            <div className="stat-label" style={{ margin: 0 }}>Badged Completions</div>
          </div>
          <div className="stat-value" style={{ color: '#E8A840' }}>{(stats?.badge_completions || 0).toLocaleString()}</div>
          <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>from {(stats?.badge_course_count || 0).toLocaleString()} courses with Credly badge</div>
        </div>
      </div>

      {/* Analytics section — expanded by default */}
      {trend.length > 0 && (
        <CollapsibleSection title="Analytics" defaultExpanded={true}>
          <div className="chart-card" style={{ marginBottom: 0 }}>
            <div className="chart-title">Monthly Completion Trend</div>
            <ResponsiveContainer width="100%" height={160}>
              <BarChart data={trend} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={v => [v.toLocaleString(), 'Completions']} />
                <Bar dataKey="count" fill="#006cbe" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CollapsibleSection>
      )}

      {/* Data section — collapsed by default */}
      <CollapsibleSection title="LevelUp Feed Data" defaultExpanded={false}>
        {/* Filters live inside Data section */}
        <div className="filter-bar" style={{ marginBottom: 16 }}>
          <div className="filter-group">
            <label className="filter-label">Audience</label>
            <select className="filter-select" value={filters.target_audience}
              onChange={e => setF('target_audience', e.target.value)} data-testid="filter-audience">
              <option value="">All</option>
              {(filterOptions.target_audiences || []).map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
          <div className="filter-group">
            <label className="filter-label">AI Course</label>
            <select className="filter-select" value={filters.ai_course}
              onChange={e => setF('ai_course', e.target.value)} data-testid="filter-ai-course">
              <option value="">All</option>
              <option value="Yes">AI Courses</option>
              <option value="No">Non-AI</option>
            </select>
          </div>
          <div className="filter-group">
            <label className="filter-label">Region</label>
            <select className="filter-select" value={filters.region}
              onChange={e => setF('region', e.target.value)} data-testid="filter-region">
              <option value="">All</option>
              {(filterOptions.regions || []).map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div className="filter-group">
            <label className="filter-label">Language</label>
            <select className="filter-select" value={filters.language}
              onChange={e => setF('language', e.target.value)} data-testid="filter-language">
              <option value="">All</option>
              {(filterOptions.languages || []).map(l => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>
          <div className="filter-group">
            <label className="filter-label">Solution Area</label>
            <select className="filter-select" value={filters.solution_area}
              onChange={e => setF('solution_area', e.target.value)} data-testid="filter-solution-area">
              <option value="">All</option>
              {(filterOptions.solution_areas || []).map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
            <button className="btn btn-primary" onClick={handleApply} disabled={loading} data-testid="apply-filters-btn">
              {loading ? <RefreshCw size={13} className="spinning" /> : null}
              Apply
            </button>
            <button className="btn btn-secondary" onClick={handleReset} data-testid="reset-filters-btn">Reset</button>
          </div>
        </div>

        {/* Table */}
        <div className="table-container">
          <div className="table-header">
            <div>
              <span className="table-title">LevelUp Course Completion Records</span>
              <span className="table-count" style={{ marginLeft: 8 }}>({total.toLocaleString()} total)</span>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                type="text" className="search-input"
                placeholder="Search course, email..."
                value={filters.search}
                onChange={e => setF('search', e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleApply()}
                data-testid="search-input"
              />
              <ExportButton
                requestUrl="/api/reports/completion/export/request"
                params={applied}
                filename="completion_report.csv"
                testId="completion-export-btn"
              />
            </div>
          </div>

          {loading ? (
            <div style={{ padding: 40, textAlign: 'center' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
          ) : data.length === 0 ? (
            <div className="empty-state"><h3>No records found</h3><p>Try adjusting your filters</p></div>
          ) : (
            <>
              <div className="table-scroll">
                <table data-testid="completion-table">
                  <thead>
                    <tr>
                      <th style={{ minWidth: 120 }}>LMS ID</th>
                      {piiAllowed && <th>Learner</th>}
                      {piiAllowed && <th>Email</th>}
                      <th>Partner</th>
                      <th>Country</th>
                      <th>Region</th>
                      <th>Course</th>
                      <th>Audience</th>
                      <th>AI</th>
                      <th title="Credly digital badge awarded on completion">Badge</th>
                      <th>Language</th>
                      <th>Completed</th>
                      <th className="td-right">%</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.map((row, i) => (
                      <tr key={row.id || i} data-testid={`completion-row-${i}`}>
                        <td style={{ fontFamily: 'monospace', fontSize: 12, whiteSpace: 'nowrap' }}>
                          {row.student_user_id || '—'}
                        </td>
                        {piiAllowed && (
                          <td title={row.student_name}>{row.student_name || '—'}</td>
                        )}
                        {piiAllowed && (
                          <td title={row.email}>{row.email || '—'}</td>
                        )}
                        <td title={row.partner_name}>{row.partner_name || '—'}</td>
                        <td>{row.country || '—'}</td>
                        <td>{row.region || '—'}</td>
                        <td title={row.class_name} style={{ maxWidth: 200 }}>{row.class_name || '—'}</td>
                        <td>
                          {row.target_audience
                            ? <span className="badge badge-blue">{row.target_audience}</span>
                            : '—'}
                        </td>
                        <td>
                          {row.ai_course === 'Yes'
                            ? <span className="badge badge-green">AI</span>
                            : <span className="badge badge-gray">—</span>}
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          {row.badge_image
                            ? <a href={row.badge_image} target="_blank" rel="noopener noreferrer" title="View Credly badge" style={{ color: '#E8A840' }}>
                                <Award size={14} />
                              </a>
                            : <span style={{ color: '#e2e8f0' }}>—</span>}
                        </td>
                        <td>{row.language || '—'}</td>
                        <td>{formatDate(row.completed_at)}</td>
                        <td className="td-right">{row.percent != null ? `${row.percent}%` : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pagination page={page} totalPages={totalPages} total={total} pageSize={50} onPage={p => fetchData(applied, p)} />
            </>
          )}
        </div>
      </CollapsibleSection>
    </div>
  );
}
